/**
 * Stack for a single profile: edit form and field selection screens.
 * ProfileFormProvider keeps form state shared across both routes.
 */
import { Stack, useLocalSearchParams } from "expo-router";
import { ActivityIndicator, Pressable, View } from "react-native";

import { useStackHeader } from "@/components/navigation/useStackHeader";
import { Text } from "@/components/ui/text";
import { ProfileFormProvider } from "@/features/profile";
import { useAuth } from "@/lib/auth";
import { useGetProfileQuery } from "@/query/apiSlice";
import { useTranslation } from "react-i18next";

function parsePipedriveId(value: string | string[] | undefined): number | null {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) {
        return null;
    }

    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }

    return parsed;
}

export default function ProfileDetailLayout() {
    const { pipedriveId: pipedriveIdParam } = useLocalSearchParams<{
        pipedriveId: string;
    }>();
    const pipedriveId = parsePipedriveId(pipedriveIdParam);
    const { isAuthenticated } = useAuth();
    const stackHeaderProps = useStackHeader();
    const { t } = useTranslation();
    const {
        data: person,
        isLoading,
        isError,
        error,
        refetch,
    } = useGetProfileQuery(pipedriveId as number, {
        skip: !isAuthenticated || pipedriveId == null,
    });

    if (pipedriveId == null) {
        return (
            <View className="flex-1 items-center justify-center px-4">
                <Text variant="muted">
                    {t("profile.error.invalidProfileSelected")}
                </Text>
            </View>
        );
    }

    // Only block on the initial load. Background refetch (e.g. refetchOnFocus)
    // must not unmount ProfileFormProvider or unsaved form edits are lost.
    if (isLoading) {
        return (
            <View className="flex-1 items-center justify-center">
                <ActivityIndicator />
            </View>
        );
    }

    if (isError) {
        return (
            <>
                <Stack.Screen
                    options={{
                        headerShown: true,
                        title: t("profile.placeholder"),
                    }}
                />
                <View className="flex-1 justify-center items-center gap-8 px-4">
                    <Text className="text-destructive">
                        {typeof error === "object" &&
                        error &&
                        "message" in error
                            ? `${t("profile.error.failedToLoadProfile")}${String((error as { message?: string }).message)}`
                            : t("profile.error.failedToLoadProfile")}
                    </Text>
                    <Pressable onPress={() => refetch()}>
                        <Text variant="label" className="text-primary">
                            {t("profile.error.tryAgain")}
                        </Text>
                    </Pressable>
                </View>
            </>
        );
    }

    if (!person) {
        return null;
    }

    return (
        <ProfileFormProvider person={person} readOnly={!person.can_edit}>
            <Stack
                // We need to configure header style and layout to match the parent layout, so we can use the same back button from the parent layout
                screenOptions={{
                    ...stackHeaderProps,
                    headerShown: true,
                }}
            ></Stack>
        </ProfileFormProvider>
    );
}
